import type { Discipline } from './naming.ts';
import type { DwellingStatus } from './units.ts';

/**
 * Design tokens (§3), in TypeScript.
 *
 * The CSS custom properties in `packages/app/src/styles/tokens.css` are
 * generated from this file's values by hand-kept mirror — but geometry,
 * legends and the engine's clear colour all read from *here*, so a swatch in
 * the layer bar and the vertex colour of the pipes it toggles can never drift.
 *
 * Sampled from the brochure. Confirm against the brand guideline before
 * shipping (§3).
 */
export const PALETTE = {
  /** App ground, deepest. */
  green900: '#16291D',
  /** Panels, brand ground. */
  green700: '#1E3A2B',
  /** Raised surfaces, hover. */
  green500: '#2C5540',
  /** Accent, rules, active state, icon strokes. */
  gold400: '#C2A24C',
  /** Light panels, plan sheets. */
  cream100: '#F2EFE9',
  /** Secondary text on dark. */
  sand300: '#D8CBB4',
} as const;

/**
 * Layer colours. Any colour that carries meaning ships with a key visible on
 * the same screen — these are the swatch values that key renders.
 */
export const DISCIPLINE_SWATCH: Readonly<Record<Discipline, string>> = {
  arch: '#D8CBB4',
  struct: '#8A9099',
  mech: '#4FA3C7',
  elec: '#E0A93B',
};

export const DISCIPLINE_LABEL_AR: Readonly<Record<Discipline, string>> = {
  arch: 'العمارة',
  struct: 'الهيكل الإنشائي',
  mech: 'السباكة',
  elec: 'الكهرباء',
};

export const DISCIPLINE_LABEL_EN: Readonly<Record<Discipline, string>> = {
  arch: 'Architecture',
  struct: 'Structure',
  mech: 'Plumbing',
  elec: 'Electrical',
};

/**
 * Availability colours. Distinguishable in hue *and* lightness, and never the
 * only signal — the legend spells each one out and the unit panel repeats it
 * in words.
 */
export const STATUS_SWATCH: Readonly<Record<DwellingStatus, string>> = {
  available: '#57B47F',
  reserved: '#C2A24C',
  sold: '#9A5B57',
};

export const STATUS_LABEL_AR: Readonly<Record<DwellingStatus, string>> = {
  available: 'متاحة',
  reserved: 'محجوزة',
  sold: 'مباعة',
};

export const STATUS_LABEL_EN: Readonly<Record<DwellingStatus, string>> = {
  available: 'Available',
  reserved: 'Reserved',
  sold: 'Sold',
};

/** Terrain and context, so the masterplan reads as ground rather than void. */
export const SITE_SWATCH = {
  terrain: '#243528',
  plot: '#2C5540',
  street: '#1A2620',
  context: '#1F2C24',
} as const;

export type Rgb01 = readonly [number, number, number];

/** `#C2A24C` → `[0.76, 0.64, 0.30]`. Throws on malformed input. */
export function hexToRgb01(hex: string): Rgb01 {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) throw new Error(`Not a 6-digit hex colour: "${hex}"`);
  const value = Number.parseInt(match[1] as string, 16);
  return [
    ((value >> 16) & 0xff) / 255,
    ((value >> 8) & 0xff) / 255,
    (value & 0xff) / 255,
  ];
}

/** sRGB hex → linear-light, for anything handed to a renderer. */
export function hexToLinear(hex: string): Rgb01 {
  const srgb = hexToRgb01(hex);
  return srgb.map((c) =>
    c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4,
  ) as unknown as Rgb01;
}
